<template>
  <div
    class="sovi-home__login px-4 pb-8 sm:px-6 md:pb-20 md:pl-0 md:pr-16"
    :class="{ '!pb-20': user?.email }"
  >
    <v-form
      ref="formRef"
      v-model="valid"
      class="mx-auto max-w-[600px]"
      @submit.prevent="onLinkRequest()"
    >
      <div ref="formContainerRef" v-resize="onResize">
        <v-slide-y-transition mode="out-in">
          <div
            v-if="!user?.email"
            key="link"
            class="sovi-home__login-form theme--light"
          >
            <sovi-logo :size="64" />

            <h1 class="font-semibold">{{ t('login') }}</h1>

            <p>
              {{ t('loginDescription') }}
            </p>

            <v-text-field
              id="email"
              v-model="email"
              :label="t('email')"
              :rules="rules.email"
              color="tertiary"
              autocomplete="off"
              name="email"
              prepend-inner-icon="mdi-email"
            />

            <v-btn
              color="primary"
              :disabled="loading"
              :loading="loading"
              block
              size="x-large"
              type="submit"
            >
              {{ t('orderLink') }}
            </v-btn>

            <div
              class="my-2 -mb-3 flex items-center gap-2 rounded-lg bg-tertiary-500/50 px-4 py-2 text-sm text-black/85 md:hidden"
            >
              <v-icon color="tertiary-600">mdi-tag</v-icon>

              <p>
                <span class="font-bold">{{ t('tryForFree') }}</span>
                {{ t('noMonthlyFees') }}
              </p>
            </div>
          </div>

          <div v-else key="logged-in" class="sovi-home__login-form">
            <sovi-logo :size="64" tertiary />

            <h1 class="mt-5 font-semibold">{{ t('welcome') }}</h1>

            <p class="mb-0">
              {{ t('loggedInUser') }} {{ user.name || user.email }}
            </p>

            <div class="pt-2">
              <v-btn
                color="primary"
                block
                size="x-large"
                :to="{ name: 'Contracts' }"
              >
                {{ t('goToContracts') }}

                <v-icon right dark icon="mdi-arrow-right" />
              </v-btn>
            </div>
          </div>
        </v-slide-y-transition>
      </div>
    </v-form>

    <sovi-status-dialog
      v-model="showStatusDialog"
      :title="
        statusDialogType === 'success'
          ? t('linkCreated')
          : t('linkCreatedError')
      "
      :subtitle="
        statusDialogType === 'success' ? t('checkEmail') : t('checkEmailError')
      "
      :type="statusDialogType"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { email as emailValidator, required } from '@/validators';
import { useAuthorizationStore } from '@/stores/authorization';
import { storeToRefs } from 'pinia';
import type { VForm } from 'vuetify/components';
import { event } from 'vue-gtag';

const { t } = useI18n();

const { create: createMagicLink } = useAuthorizationStore();

const { user } = storeToRefs(useAuthorizationStore());

const emit = defineEmits<{
  resize: [value: string];
}>();

const loading = ref(false);

const showStatusDialog = ref(false);

const statusDialogType = ref<'success' | 'failure'>('success');

const valid = ref(false);

const formRef = ref<InstanceType<typeof VForm> | null>(null);

const email = ref('');

const formContainerRef = ref<HTMLDivElement | null>(null);

const rules = computed(() => ({
  email: [required(t('emailRequired')), emailValidator(t('emailInvalid'))],
}));

const onRequestLink = async (token: string) => {
  try {
    loading.value = true;

    const response = await createMagicLink({ email: email.value, token });

    loading.value = false;

    formRef.value?.reset();
    formRef.value?.resetValidation();

    return response;
  } catch (error) {
    loading.value = false;
    throw new Error('Error loading the link');
  }
};

const onResize = () => {
  if (!formContainerRef.value) return;

  const { height } = formContainerRef.value.getBoundingClientRect();

  emit('resize', `${height}px`);
};

const onLinkRequest = async () => {
  formRef.value?.validate();

  if (!valid.value) return;

  try {
    const token = await window.grecaptcha.execute(
      '6LdrvAspAAAAALZn5Cqp3qCMDvrIfvNY3A_Y8x__',
      {
        action: 'submit',
      },
    );

    const { success } = await onRequestLink(token);

    statusDialogType.value = success ? 'success' : 'failure';

    showStatusDialog.value = true;

    formRef.value?.reset();

    const trackingConsent = localStorage.getItem('allowTracking');

    if (trackingConsent === 'true') {
      event('conversion', {
        send_to: 'AW-16774635677/Np9BCNaW8rAaEJ3B4r4-',
        value: 1.0,
        currency: 'EUR',
      });
    }
  } catch (error) {
    console.error(error);
  }
};
</script>

<i18n lang="json">
{
  "fi": {
    "email": "Sähköpostiosoite",
    "emailInvalid": "Virheellinen sähköpostiosoite",
    "emailRequired": "Anna sähköpostiosoitteesi, ole hyvä",
    "goToContracts": "Siirry sopimuksiin",
    "linkCreated": "Kirjautumislinkki lähetetty.",
    "linkCreatedError": "Virhe kirjautumislinkin luomisessa",
    "loggedInUser": "Kirjautunut käyttäjä: ",
    "login": "Aloita tästä",
    "loginDescription": "Syötä sähköpostiosoitteesi oheiseen kenttään ja tilaa kirjautumislinkki!",
    "orderLink": "Tilaa kirjautumislinkki",
    "welcome": "Tervetuloa takaisin",
    "checkEmail": "Avaa sähköpostiisi tullut linkki kirjautuaksesi sisään.",
    "checkEmailError": "Ole hyvä ja yritä myöhemmin uudelleen",
    "tryForFree": "Kokeile ilmaiseksi!",
    "noMonthlyFees": "Ei kuukausimaksua!"
  },
  "en": {
    "email": "Email address",
    "emailInvalid": "Invalid email",
    "emailRequired": "Input your email please",
    "goToContracts": "Go to contracts",
    "linkCreated": "The login link has been sent.",
    "linkCreatedError": "An error occurred while creating the link",
    "loggedInUser": "Logged in user: ",
    "login": "Start here",
    "loginDescription": "Enter your email address to the field below to order a login link!",
    "orderLink": "Request a login link",
    "welcome": "Welcome back",
    "checkEmail": "Use the link in your email to login.",
    "checkEmailError": "Please try again later",
    "tryForFree": "Try for free!",
    "noMonthlyFees": "No monthly fees!"
  }
}
</i18n>
